import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/Auth";
import { Helmet } from "react-helmet-async";
import {
  Button,
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Stack,
} from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import Banner from "../components/Banner";
import { DataGrid } from "@mui/x-data-grid";
import { ticketFeedColumns } from "../internals/data/TicketFeedGrid";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import api from "../helpers/api";
import PriceOverTimeChart from "../components/home/PriceOverTimeChart";
import AppTheme from "../shared-theme/AppTheme";
import { FeedEvent } from "../models/TicketFeed";

const Home: React.FC = () => {
  const { user } = useAuth();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [category, setCategory] = useState<string | null>("concert");
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [showMany, setShowMany] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<FeedEvent[]>([]);
  const graphRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page + 1),
      limit: String(pageSize),
    });
    if (search) params.append("search", search);
    if (category) params.append("category", category);
    api
      .get(`/analytics/feed?${params.toString()}`)
      .then((res) => {
        const data = res.data;
        setRows(
          data.processedFeedData.map((item: any, idx: number) => ({
            id: item.eventId || idx + page * pageSize,
            ...item,
          }))
        );
        if (data.fullPagination === "many") {
          setRowCount(1000);
          setShowMany(true);
        } else {
          setRowCount(data.fullPagination || 0);
          setShowMany(false);
        }
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, search, category]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      setPage(0);
      setSearch(searchInput);
    }
  };

  const handleSearchIconClick = () => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    setPage(0);
    setSearch(searchInput);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        background: `
        url('/event-background.webp'),
        radial-gradient(circle at top left, #041C2C 0%, #22143C 100%)
      `,
        backgroundBlendMode: "soft-light, normal",
        backgroundSize: "cover, cover, cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        color: "#E3D9D6",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.05)",
          pointerEvents: "none",
        },
      }}
    >
      <>
        {/* Meta tags for SEO */}
        <Helmet>
          <title>Event Spy - The event resale ticket price tracker app</title>
          <meta
            name="description"
            content="Never overpay for resale tickets again. Get real-time price alerts and analytics for tickets to all of your favourite events with our resale ticket price tracker app"
          />
        </Helmet>
      </>
      <Banner />
      {/* Hero Section */}
      <Box
        sx={{
          py: 8,
          textAlign: "center",
          pt: "64px",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            paddingTop="60px"
            fontWeight={700}
          >
            Never overpay for tickets again
          </Typography>
          <Typography variant="h5" component="h2" gutterBottom>
            Get real-time price alerts and analytics for tickets to all of your
            favourite events with our resale ticket price tracker app
          </Typography>
          {user ? (
            <Box
              sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}
            >
              <Link to="/console" style={{ textDecoration: "none" }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    background:
                      "linear-gradient(45deg,rgba(10, 55, 113, 0.6) 30%,rgba(17, 83, 158, 0.6) 90%)",
                    borderRadius: "50px",
                    padding: "12px 35px",
                    textTransform: "none",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    boxShadow: "0 0 15px rgba(255, 255, 255, 0.2)",
                    "&:hover": {
                      background:
                        "linear-gradient(45deg,rgba(10, 42, 74, 0.4) 30%,rgba(4, 28, 44, 0.4) 90%)",
                      boxShadow: "0 0 20px rgba(255, 255, 255, 0.3)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    },
                  }}
                >
                  Go To Console
                </Button>
              </Link>
            </Box>
          ) : (
            <div>
              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                }}
              >
                <Link to="/signup" style={{ textDecoration: "none" }}>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      background:
                        "linear-gradient(45deg,rgba(10, 55, 113, 0.6) 30%,rgba(17, 83, 158, 0.6) 90%)",
                      borderRadius: "50px",
                      padding: "12px 35px",
                      textTransform: "none",
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      boxShadow: "0 0 15px rgba(255, 255, 255, 0.2)",
                      "&:hover": {
                        background:
                          "linear-gradient(45deg, #0A2A4A 30%, #041C2C 90%)",
                        boxShadow: "0 0 20px rgba(255, 255, 255, 0.3)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  >
                    Start Tracking Free
                  </Button>
                </Link>
              </Box>
            </div>
          )}
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ pt: 2, pb: 6 }}>
        <Grid container spacing={{ xs: 6, md: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                pt: 3,
                pb: 1,
                height: "100%",
                textAlign: "center",
                background: "rgba(17, 25, 44, 0.7)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: "0 0 20px rgba(0, 0, 0, 0.3)",
                transition: "transform 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 0 30px rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                alignItems="center"
                mb={2}
              >
                <NotificationsActiveIcon
                  sx={{ fontSize: 40, color: "#4A9EFF" }}
                />
              </Stack>
              <Typography
                variant="h6"
                component="h2"
                gutterBottom
                sx={{ color: "#E3D9D6" }}
              >
                Ticket Price Drop Alerts
              </Typography>
              <Typography
                component="p"
                sx={{
                  color: "rgba(227, 217, 214, 0.7)",
                  mb: 2,
                  padding: "0 16px",
                }}
              >
                Get instant notifications when resale ticket prices drop below
                your target
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                alignItems="center"
                sx={{ pt: 2 }}
              >
                <Box
                  sx={{
                    background:
                      "linear-gradient(45deg,rgba(37, 211, 101, 0.50),rgba(18, 140, 126, 0.5))",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 16, color: "white" }} />
                  <Typography
                    sx={{ color: "white", fontSize: "0.8rem", fontWeight: 600 }}
                  >
                    WhatsApp
                  </Typography>
                </Box>
                <Box
                  sx={{
                    background:
                      "linear-gradient(45deg,rgb(28, 16, 73),rgb(44, 18, 71))",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <EmailIcon sx={{ fontSize: 16, color: "white" }} />
                  <Typography
                    sx={{ color: "white", fontSize: "0.8rem", fontWeight: 600 }}
                  >
                    Email
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                pt: 3,
                pb: 1,
                height: "100%",
                textAlign: "center",
                background: "rgba(17, 25, 44, 0.7)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: "0 0 20px rgba(0, 0, 0, 0.3)",
                transition: "transform 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 0 30px rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <ShowChartIcon sx={{ fontSize: 40, color: "#4A9EFF", mb: 2 }} />
              <Typography
                variant="h6"
                component="h2"
                gutterBottom
                sx={{ color: "#E3D9D6" }}
              >
                Analytics
              </Typography>
              <Typography
                component="p"
                sx={{ color: "rgba(227, 217, 214, 0.7)", padding: "0 16px" }}
              >
                View event resale ticket price history and number of listings
                over time (on supported sites) to make informed buying decisions
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* DataGrid Section */}
      <Container maxWidth="lg" sx={{ pt: 8, pb: 12, height: 790 }}>
        <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 700 }}>
          Live Event Feed
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", sm: "space-between" },
            alignItems: "flex-end",
            mb: 2,
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: { xs: "flex-start", sm: "flex-start" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {["concert", "sport", "theater"].map((cat) => (
              <Button
                key={cat}
                variant={category === cat ? "contained" : "outlined"}
                size="small"
                onClick={() => setCategory(category === cat ? null : cat)}
                sx={{
                  textTransform: "capitalize",
                  fontWeight: 600,
                  borderRadius: 2,
                  background:
                    category === cat
                      ? "linear-gradient(45deg, #3B82F6 30%, #60A5FA 90%)"
                      : "rgba(10, 42, 74, 0.95)",
                  color: category === cat ? "white" : "#d7cdcb",
                  borderColor: "#3B82F6",
                  "&:hover": {
                    background:
                      category === cat
                        ? "linear-gradient(45deg, #2563EB 30%, #60A5FA 90%)"
                        : "rgba(30, 64, 175, 0.85)",
                  },
                  px: 2,
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: 0, sm: undefined },
                }}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </Button>
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search events..."
              value={searchInput}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              sx={{
                minWidth: 260,
                background: "rgba(10, 42, 74, 0.95)",
                borderRadius: 2,
                input: { color: "white" },
                width: { xs: "100%", sm: "auto" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#3B82F6",
                  },
                },
              }}
              InputProps={{
                style: { color: "white" },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="search"
                      onClick={handleSearchIconClick}
                      edge="end"
                      sx={{ color: "#E3D9D6" }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                style: { color: "#AEE2FF" },
              }}
            />
          </Box>
        </Box>
        <Paper
          sx={{
            height: "100%",
            width: "100%",
            background: "rgba(17, 25, 44, 0.85)",
            color: "white",
            borderRadius: 3,
            boxShadow: "0 0 20px 0 rgba(59,130,246,0.10)",
            border: "1.5px solid rgba(255, 255, 255, 0.1)",
            overflow: "hidden",
          }}
        >
          <DataGrid
            rows={rows}
            columns={ticketFeedColumns}
            loading={loading}
            pagination
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={({ page: newPage }) => {
              setPage(newPage);
            }}
            autoHeight={false}
            hideFooterSelectedRowCount={true}
            disableColumnSelector
            disableColumnFilter
            disableColumnMenu
            getRowHeight={() => 52}
            onRowSelectionModelChange={(ids) => {
              const selectedId =
                Array.isArray(ids) && ids.length > 0 ? ids[0] : null;
              const selectedRow = rows.find((row) => row.id === selectedId);
              setSelectedEvent(selectedRow ? [selectedRow] : []);
              if (selectedRow && graphRef.current) {
                setTimeout(() => {
                  graphRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }, 100);
              }
            }}
            sx={{
              background: "transparent",
              color: "white",
              border: "none",
              fontFamily: "inherit",
              "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-checkboxInput":
                {
                  display: "none",
                },
              "& .MuiDataGrid-columnHeaders": {
                background: "rgba(10, 42, 74, 0.95)",
                color: "white",
                fontWeight: 700,
                fontSize: "1.05rem",
                letterSpacing: "0.03em",
                borderBottom: "2px solid rgba(255, 255, 255, 0.1)",
              },
              "& .MuiDataGrid-sortIcon": {
                color: "white !important",
              },
              "& .MuiDataGrid-cell": {
                background: "rgba(17, 25, 44, 0.85)",
                color: "white",
                borderBottom: "1.5px solid rgba(255, 255, 255, 0.1)",
                fontSize: "1.01rem",
                borderRight: "1px solid rgba(255, 255, 255, 0.1)",
              },
              "& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell": {
                borderBottom: "none",
              },
              "& .MuiDataGrid-columnHeader": {
                borderRight: "1px solid rgba(255, 255, 255, 0.1)",
              },
              "& .MuiDataGrid-overlay": {
                background: "rgba(0, 0, 0, 0.75)",
              },
              "& .MuiDataGrid-row:hover": {
                background: "rgba(59,130,246,0.08)",
              },
              "& .MuiDataGrid-footerContainer": {
                background: "rgba(10, 42, 74, 0.95)",
                color: "white",
                borderTop: "2px solid rgba(255, 255, 255, 0.1)", // changed
              },
              "& .MuiTablePagination-root, & .MuiTablePagination-toolbar, & .MuiTablePagination-selectLabel, & .MuiTablePagination-input, & .MuiTablePagination-displayedRows":
                {
                  color: "white",
                },
              "& .MuiDataGrid-selectedRowCount": {
                color: "white",
              },
              "& .MuiDataGrid-iconSeparator": {
                display: "none",
              },
              minHeight: 52 * 10 + 56 + 56,
              maxHeight: 52 * 10 + 56 + 56,
            }}
            slotProps={{
              baseButton: {
                sx: {
                  color: "white",
                  "&:hover": { color: "#3B82F6" },
                },
              },
              pagination: {
                labelDisplayedRows: ({ from, to, count }) =>
                  showMany
                    ? `${from}–${to} of >1000`
                    : `${from}–${to} of ${
                        count !== -1 ? count : `more than ${to}`
                      }`,
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.1rem",
                  }}
                >
                  No results found
                </Box>
              ),
            }}
          />
        </Paper>
      </Container>

      <Container sx={{ pt: 12 }} ref={graphRef}>
        <Typography variant="h5" component="h4" sx={{ mb: 2, fontWeight: 700 }}>
          Get-In Daily Price History
        </Typography>
        <AppTheme>
          <PriceOverTimeChart
            eventId={selectedEvent[0]?.eventId}
            eventName={selectedEvent[0]?.eventName}
            eventDate={selectedEvent[0]?.eventDate}
            location={selectedEvent[0]?.location}
          />
        </AppTheme>
      </Container>

      {/* SEO section */}
      <Box
        sx={{
          mt: 8,
          py: 8,
          textAlign: "left",
          pt: "64px",
        }}
      >
        <Container maxWidth="lg" sx={{ pb: 8 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            <b>Fair Prices for True Fans</b>
          </Typography>
          <Typography variant="h5" component="p" gutterBottom>
            At Event Spy, we believe true fans should never be priced out of
            seeing their favorite artists, sports teams, or live events.
          </Typography>
          <Typography variant="h5" component="p" gutterBottom>
            In an era where ticket scalping is more rampant than ever, it’s
            unfair that missing an initial sale means facing sky-high resale
            prices.
          </Typography>
          <Typography variant="h5" component="p" gutterBottom>
            That’s why we built Event Spy — the ultimate ticket price tracking
            app.
          </Typography>
          <Typography variant="h5" component="p" gutterBottom>
            We keep you informed, alert you to price drops, and help you find
            tickets at the price you’re willing to pay.
          </Typography>
          <Typography variant="h5" component="p">
            <b>Stay ahead of the game. Sign up and start tracking today.</b>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
